import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Button,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoIcon from "@mui/icons-material/Info";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useRef, useState, useMemo } from "react";
import PropTypes from 'prop-types';
import { ALLOWED_FILE_TYPES, ALLOWED_MIME_TYPES, MAX_FILE_SIZE, formatFileSize } from "../constants";
import { PricingSummary } from "..";
import { validateContactFileWithVariables, checkFileColumnCount } from "../../../../utils/campaign-file-validator.utils";
import { extractTemplateVariables } from "../../../../utils/template-variable-extractor.utils";
import { ImportReportModal } from "..";

export const AudienceSection = ({
  value,
  onChange,
  uploadedFile,
  onFileChange,
  pricing,
  loadingPricing,
  onImportReport,
  importReport,
  onServerValidate,
  onSnackbar,
  selectedTemplate,
  onValidationResult,
}) => {
  const fileInputRef = useRef(null);
  const pendingFileRef = useRef(null);
  const originalFileNameRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationProgress, setValidationProgress] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [columnsValidated, setColumnsValidated] = useState(false);

  const templateVariables = useMemo(
    () => extractTemplateVariables(selectedTemplate),
    [selectedTemplate]
  );

  const showSnackbar = (message, severity = 'error') => {
    if (onSnackbar) {
      onSnackbar(message, severity);
    }
  };

  const clearFile = () => {
    onFileChange(null);
    setValidationResult(null);
    setValidationError(null);
    setColumnsValidated(false);
    setShowImportModal(false);
    if (onImportReport) onImportReport(null);
    originalFileNameRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isValidFileType = (file) => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    const hasValidExtension = ALLOWED_FILE_TYPES.some((ext) =>
      fileName.endsWith(ext)
    );
    const hasValidMimeType = ALLOWED_MIME_TYPES.includes(fileType);

    return hasValidExtension || hasValidMimeType;
  };


  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validacion de tipo y tamano (se mantiene)
    if (!isValidFileType(file)) {
      showSnackbar(
        `Tipo de archivo no permitido. Solo se aceptan: ${ALLOWED_FILE_TYPES.join(', ')}`,
        'error'
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(
        `El archivo es demasiado grande. Tamaño máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`,
        'error'
      );
      return;
    }

    // Limpiar errores previos
    setValidationError(null);
    setValidationResult(null);
    setColumnsValidated(false);

    // Validacion servidor con fallback a cliente
    setIsValidating(true);
    setValidationProgress(0);

    if (onServerValidate) {
      try {
        setValidationProgress(0);
        pendingFileRef.current = file;
        originalFileNameRef.current = file.name;

        // Pre-check de columnas (para templates con variables) antes de llamar al servidor
        await checkFileColumnCount(file, templateVariables);
        setColumnsValidated(true);

        const report = await onServerValidate(file, setValidationProgress);
        if (onImportReport) onImportReport(report);
        setShowImportModal(true);
        setIsValidating(false);
        return;
      } catch (serverError) {
        // Si es error de columnas, mostrarlo inline sobre el archivo, sin limpiarlo
        if (serverError.message?.includes('columna')) {
          setValidationError(serverError.message);
          setColumnsValidated(false);
          // NO limpiar el archivo: el usuario ve el error + el nombre del archivo
          setIsValidating(false);
          return;
        }
        // Otros errores del servidor: log para debug
        console.warn('Server validation failed, using client fallback:', serverError.message);
      }
    }

    // Fallback a validacion cliente
    try {
      originalFileNameRef.current = file.name;
      const result = await validateContactFileWithVariables(file, templateVariables);
      setValidationResult(result);
      onFileChange(result.validatedFile);
      if (onValidationResult) onValidationResult(result);

      const parts = [`Archivo validado: ${result.validPhones.length} teléfonos válidos`];
      if (result.invalidCount > 0) parts.push(`${result.invalidCount} inválidos`);
      if (result.duplicateCount > 0) parts.push(`${result.duplicateCount} duplicados eliminados`);
      if (result.hasVariables) parts.push(`${result.variableRows.length} filas con variables`);

      const msg = parts.join(', ') + '.';
      showSnackbar(msg, result.invalidCount > 0 ? 'warning' : 'success');
    } catch (error) {
      console.error('Error al validar archivo:', error);
      setValidationError(error.message || 'Error al validar el archivo. Intente de nuevo.');
      onFileChange(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmImport = async () => {
    setShowImportModal(false);
    setIsValidating(true);

    try {
      const result = await validateContactFileWithVariables(pendingFileRef.current, templateVariables);
      setValidationResult(result);
      onFileChange(result.validatedFile);
      if (onValidationResult) onValidationResult(result);
    } catch (error) {
      console.warn('Error al generar archivo limpio, usando archivo original:', error.message);
      onFileChange(pendingFileRef.current);
    }

    pendingFileRef.current = null;
    setIsValidating(false);
  };

  const handleCancelImport = () => {
    setShowImportModal(false);
    if (onImportReport) onImportReport(null);
    pendingFileRef.current = null;
    clearFile();
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    event.target.value = '';
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    clearFile();
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/templates/plantilla_contactos.xlsx';
    link.download = 'plantilla_contactos.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <FormControl component="fieldset" fullWidth>
        <FormLabel
          component="h3"
          required
          sx={{
            mb: 1,
            color: "text.primary",
            "&.Mui-focused": {
              color: "text.primary",
            },
          }}
        >
          Audiencia
        </FormLabel>

        {/* Solo carga de archivo */}
        <Box sx={{ mt: 1 }}>

          {/* --- VARIABLES INFO BLOCK --- */}
            {templateVariables.length > 0 && (
              <Paper
                variant="outlined"
                sx={(theme) => ({
                  p: 2,
                  mb: 2,
                  backgroundColor: alpha(
                    columnsValidated
                      ? theme.palette.success.main
                      : validationError
                        ? theme.palette.error.main
                        : theme.palette.info.main,
                    0.08
                  ),
                  borderColor: columnsValidated
                    ? 'success.main'
                    : validationError
                      ? 'error.main'
                      : 'info.main',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoIcon
                    color={columnsValidated ? 'success' : validationError ? 'error' : 'info'}
                    fontSize="small"
                  />
                  <Typography variant="subtitle2" color={columnsValidated ? 'success.main' : validationError ? 'error.main' : 'info.main'}>
                    {columnsValidated
                      ? 'El archivo cumple con las columnas requeridas para enviar el template'
                      : `El template requiere ${templateVariables.length} variable${templateVariables.length > 1 ? 's' : ''}`
                    }
                  </Typography>
                </Box>
                {!validationError && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 4 }}>
                    <Typography variant="body2">
                      <strong>Columna 1:</strong> Telefono
                    </Typography>
                    {templateVariables.map((v, i) => (
                      <Typography variant="body2" key={v}>
                        <strong>Columna {i + 2}:</strong> {`{{${v}}}`}
                      </Typography>
                    ))}
                  </Box>
                )}
                {validationError && !columnsValidated && (
                  <Typography variant="body2" color="error.main" sx={{ ml: 4, fontWeight: 500 }}>
                    {validationError}
                  </Typography>
                )}
                {!columnsValidated && !validationError && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    El archivo debe tener {templateVariables.length + 1} columnas con los valores correspondientes.
                  </Typography>
                )}
              </Paper>
            )}
            {/* --- END VARIABLES INFO BLOCK --- */}

            <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
              <FileDownloadIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Button
                component="button"
                variant="body2"
                onClick={handleDownloadTemplate}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  color: "primary.main",
                  paddingLeft: 0.5,
                }}
              >
                Descargar Plantilla
              </Button>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_FILE_TYPES.join(",")}
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />

            {/* Área de drag & drop */}
            {!uploadedFile ? (
              <Box justifyContent="center" alignItems="center" display="flex">
                <Paper
                  variant="outlined"
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    px: 1,
                    py: 2,
                    textAlign: "center",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: isDragging ? "primary.main" : "grey.300",
                    backgroundColor: isDragging ? "action.hover" : "background.paper",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                    width: "100%",
                  }}
                  onClick={handleBrowseClick}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      color: isDragging ? 'primary.main' : 'grey.400',
                      mb: 1,
                    }}
                  />
                  <Typography variant="body1" gutterBottom>
                    Arrastre y suelte archivos aquí, o haga clic en cargar
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, mb: 1 }} color="text.secondary">
                    Tipos: {ALLOWED_FILE_TYPES.join(', ')} | Máx: {MAX_FILE_SIZE / 1024 / 1024}MB
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrowseClick();
                    }}
                  >
                    Cargar documento
                  </Button>
                </Paper>
              </Box>
            ) : (
              // Vista del archivo que cargue
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderColor: 'success.main',
                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                  width: '100%',
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <DescriptionIcon color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {originalFileNameRef.current || uploadedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(uploadedFile.size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  color="error"
                  aria-label="eliminar archivo"
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}

            {/* Loading durante validación servidor */}
            {isValidating && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {validationProgress > 0
                      ? `Validando archivo... ${validationProgress}%`
                      : 'Validando archivo...'}
                  </Typography>
                </Box>
                {validationProgress > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={validationProgress}
                    sx={{ maxWidth: 300, mx: 'auto' }}
                  />
                )}
              </Box>
            )}

            {/* Resumen de validación */}
            {validationResult && !isValidating && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1.5,
                  borderColor: validationResult.invalidCount > 0 ? 'warning.main' : 'success.main',
                  backgroundColor: (theme) => alpha(theme.palette[validationResult.invalidCount > 0 ? 'warning' : 'success'].main, 0.08),
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label={`${validationResult.validPhones.length} válidos`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  {validationResult.invalidCount > 0 && (
                    <Chip
                      label={`${validationResult.invalidCount} inválidos`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {validationResult.duplicateCount > 0 && (
                    <Chip
                      label={`${validationResult.duplicateCount} duplicados`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Paper>
            )}

            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              *Asegúrate de cargar el formato especificado y de no sobrepasar el
              peso del archivo.
              <br />
              Si el documento contiene imágenes, asegúrate de removerlas.
            </Typography>
          </Box>
      </FormControl>

        <PricingSummary pricing={pricing} loading={loadingPricing} compact />

      {showImportModal && importReport && (
        <ImportReportModal
          open={showImportModal}
          onClose={handleCancelImport}
          report={importReport}
          onConfirm={handleConfirmImport}
          validationResult={validationResult}
        />
      )}

      {/* Snackbar is rendered by parent CampaignForm */}
    </Box>
  );
};

AudienceSection.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  uploadedFile: PropTypes.instanceOf(File),
  onFileChange: PropTypes.func.isRequired,
  pricing: PropTypes.object,
  loadingPricing: PropTypes.bool,
  onImportReport: PropTypes.func,
  importReport: PropTypes.object,
  onServerValidate: PropTypes.func,
  onSnackbar: PropTypes.func,
  selectedTemplate: PropTypes.object,
  onValidationResult: PropTypes.func,
};
